import { Bug, Copy, RotateCw, WifiOff } from "lucide-react";
import { useEffect } from "react";
import { useRouteError } from "react-router";
import { Button } from "@/components/ui/button";
import { isChunkLoadError, reloadOnceForChunkError } from "@/lib/chunkLoad";
import { ISSUE_URL, UI_VERSION } from "@/lib/buildInfo";

function ReloadButton() {
  return (
    <Button onClick={() => window.location.reload()}>
      <RotateCw aria-hidden /> Reload
    </Button>
  );
}

export function CrashBoundary() {
  const error = useRouteError();
  const unloaded = isChunkLoadError(error);
  useEffect(() => {
    if (unloaded) reloadOnceForChunkError();
  }, [unloaded]);
  if (unloaded) {
    return (
      <section role="alert" className="mx-auto mt-16 flex max-w-md flex-col items-center gap-4 text-center">
        <WifiOff aria-hidden className="size-10 text-muted-foreground" />
        <h1 className="text-2xl font-semibold">This page didn't load</h1>
        <p className="text-muted-foreground">
          The connection dropped while the page was loading, or a newer ATS UI was published. Reload to try again.
        </p>
        <ReloadButton />
      </section>
    );
  }
  const details = JSON.stringify({
    ui: UI_VERSION,
    path: window.location.pathname,
    error: error instanceof Error ? error.message : String(error),
  });
  return (
    <section role="alert" className="mx-auto mt-16 flex max-w-md flex-col items-center gap-4 text-center">
      <Bug aria-hidden className="size-10 text-tone-orange-fg" />
      <h1 className="text-2xl font-semibold">Something broke on this page</h1>
      <p className="text-muted-foreground">Reload to try again. If it keeps happening, report it with the error details.</p>
      <div className="flex flex-wrap justify-center gap-2">
        <ReloadButton />
        <Button variant="outline" onClick={() => void navigator.clipboard.writeText(details)}>
          <Copy aria-hidden /> Copy error details
        </Button>
        <Button variant="ghost" asChild>
          <a href={ISSUE_URL} target="_blank" rel="noopener noreferrer">
            Report an issue
          </a>
        </Button>
      </div>
    </section>
  );
}
